import { useState } from "react";

export type JobPhoto = {
  id: string;
  job_id: string;
  photo_url: string;
  thumbnail_url: string | null;
  filename: string | null;
  category: string | null;
  is_selected: boolean;
  is_favorite: boolean;
  selection_note: string | null;
};

export type PhotoJob = {
  id: string;
  title: string;
  job_type: string;
  shoot_date: string | null;
};

type FilterView = "all" | "selected" | "favorite";

/* ─── NoteField ─────────────────────────────────────────────── */
function NoteField({
  photoId,
  note,
  onSave,
}: {
  photoId: string;
  note: string | null;
  onSave: (photoId: string, note: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(note ?? "");

  const handleBlur = () => {
    setEditing(false);
    onSave(photoId, val);
  };

  if (editing) {
    return (
      <input
        type="text"
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={(e) => { if (e.key === "Enter") handleBlur(); }}
        placeholder="Add a note…"
        className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-700 placeholder-slate-400 outline-none focus:border-[#C8A96E]/50 transition-colors"
      />
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="w-full text-left text-xs text-slate-400 hover:text-slate-600 truncate cursor-pointer transition-colors"
    >
      {val ? val : <span className="italic">Add note…</span>}
    </button>
  );
}

/* ─── PhotoCard ─────────────────────────────────────────────── */
function PhotoCard({
  photo,
  onToggleSelected,
  onToggleFavorite,
  onNoteUpdate,
  updatingPhotoId,
}: {
  photo: JobPhoto;
  onToggleSelected: (photoId: string, field: "is_selected", currentVal: boolean) => void;
  onToggleFavorite: (photoId: string, field: "is_favorite", currentVal: boolean) => void;
  onNoteUpdate: (photoId: string, note: string) => void;
  updatingPhotoId: string | null;
}) {
  const isBusy = updatingPhotoId === photo.id;

  return (
    <div className={`bg-white rounded-xl border overflow-hidden transition-all ${photo.is_selected ? "border-[#C8A96E]/40 shadow-sm" : "border-slate-200"}`}>
      <div className="aspect-square bg-slate-50 overflow-hidden relative">
        <img
          src={photo.thumbnail_url ?? photo.photo_url}
          alt={photo.filename ?? "Photo"}
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
        {/* Badges overlay */}
        <div className="absolute top-2 right-2 flex flex-col gap-1">
          {photo.is_selected && (
            <span className="bg-[#C8A96E] text-black text-[10px] font-bold px-2 py-0.5 rounded-full">
              Selected
            </span>
          )}
          {photo.is_favorite && (
            <span className="bg-red-400 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
              <i className="ri-heart-fill text-[10px]"></i> Fav
            </span>
          )}
        </div>
        {/* Category badge */}
        {photo.category && (
          <div className="absolute bottom-2 left-2">
            <span className="bg-black/60 text-white/90 text-[10px] px-2 py-0.5 rounded-full">
              {photo.category}
            </span>
          </div>
        )}
      </div>

      <div className="p-3 space-y-2">
        {photo.filename && (
          <p className="text-slate-500 text-xs truncate">{photo.filename}</p>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => onToggleSelected(photo.id, "is_selected", photo.is_selected)}
            disabled={isBusy}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer disabled:opacity-50 whitespace-nowrap ${
              photo.is_selected
                ? "bg-[#C8A96E]/15 text-[#C8A96E] border border-[#C8A96E]/20"
                : "bg-slate-50 text-slate-500 hover:bg-[#C8A96E]/10 hover:text-[#C8A96E] border border-slate-200"
            }`}
          >
            {isBusy ? (
              <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={photo.is_selected ? "ri-checkbox-circle-fill" : "ri-checkbox-circle-line"}></i>
                </span>
                {photo.is_selected ? "Selected" : "Select"}
              </>
            )}
          </button>
          <button
            onClick={() => onToggleFavorite(photo.id, "is_favorite", photo.is_favorite)}
            disabled={isBusy}
            className={`w-10 h-9 flex items-center justify-center rounded-lg text-sm transition-all cursor-pointer disabled:opacity-50 ${
              photo.is_favorite
                ? "bg-red-50 text-red-500 border border-red-200"
                : "bg-slate-50 text-slate-400 hover:text-red-500 hover:bg-red-50 border border-slate-200"
            }`}
          >
            <i className={photo.is_favorite ? "ri-heart-fill" : "ri-heart-line"}></i>
          </button>
        </div>

        {/* Note */}
        <NoteField
          photoId={photo.id}
          note={photo.selection_note}
          onSave={onNoteUpdate}
        />
      </div>
    </div>
  );
}

/* ─── Main ──────────────────────────────────────────────────── */
interface Props {
  jobs: PhotoJob[];
  photos: JobPhoto[];
  updatingPhotoId: string | null;
  onPhotoToggle: (
    photoId: string,
    field: "is_selected" | "is_favorite",
    currentVal: boolean,
  ) => void;
  onNoteUpdate: (photoId: string, note: string) => void;
}

export default function PhotoSelectionSection({
  jobs,
  photos,
  updatingPhotoId,
  onPhotoToggle,
  onNoteUpdate,
}: Props) {
  const [filter, setFilter] = useState<FilterView>("all");

  const allPhotos = photos;
  const selectedCount = allPhotos.filter((p) => p.is_selected).length;
  const favoriteCount = allPhotos.filter((p) => p.is_favorite).length;

  const filteredPhotos = allPhotos.filter((p) => {
    if (filter === "selected") return p.is_selected;
    if (filter === "favorite") return p.is_favorite;
    return true;
  });

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-[#0f172a] font-semibold text-base flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center text-[#C8A96E]">
            <i className="ri-image-2-line"></i>
          </span>
          Photo Selection
        </h2>

        {/* Filters + counts */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-3 text-xs text-slate-500 mr-2">
            <span className="text-[#C8A96E] font-semibold">{selectedCount} selected</span>
            <span className="text-red-400 font-semibold">{favoriteCount} favorites</span>
          </div>
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
            {(["all", "selected", "favorite"] as FilterView[]).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded-md text-xs font-medium capitalize transition-colors cursor-pointer whitespace-nowrap ${
                  filter === f
                    ? "bg-[#C8A96E] text-black"
                    : "text-slate-400 hover:text-slate-700"
                }`}
              >
                {f === "all" ? "All" : f === "selected" ? "Selected" : "Favorites"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Job groupings */}
      {jobs.map((job) => {
        const jobPhotos = filteredPhotos.filter((p) => p.job_id === job.id);
        if (jobPhotos.length === 0) return null;

        const jobSelectedCount = photos.filter((p) => p.job_id === job.id && p.is_selected).length;
        const jobTotal = photos.filter((p) => p.job_id === job.id).length;

        return (
          <div key={job.id} className="mb-6">
            {/* Job sub-header */}
            <div className="flex items-center justify-between mb-3 px-1">
              <div>
                <p className="text-[#0f172a] font-medium text-sm">{job.title}</p>
                <p className="text-slate-400 text-xs mt-0.5">
                  {job.job_type}
                  {job.shoot_date
                    ? ` · ${new Date(job.shoot_date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}`
                    : ""}
                </p>
              </div>
              <span className="text-slate-400 text-xs">
                {jobSelectedCount} / {jobTotal} selected
              </span>
            </div>

            {/* Photo grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {jobPhotos.map((photo) => (
                <PhotoCard
                  key={photo.id}
                  photo={photo}
                  onToggleSelected={onPhotoToggle}
                  onToggleFavorite={onPhotoToggle}
                  onNoteUpdate={onNoteUpdate}
                  updatingPhotoId={updatingPhotoId}
                />
              ))}
            </div>
          </div>
        );
      })}

      {filteredPhotos.length === 0 && (
        <div className="flex flex-col items-center gap-3 py-12 bg-white rounded-xl border border-slate-200">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-slate-100">
            <i className="ri-image-2-line text-slate-400 text-2xl"></i>
          </div>
          <p className="text-slate-400 text-sm">
            {photos.length === 0
              ? "No photos available yet."
              : "No photos match this filter."}
          </p>
        </div>
      )}
    </div>
  );
}